import React, { useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Globe,
  Lock,
  ExternalLink,
  Layers,
  FileText,
  AlertCircle,
  Clock,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import type { LearnSectionSummary, LearnTopic } from '@/client/types/learn';
import {
  useAdminLearnTopicQuery,
  useDeleteLearnSectionMutation,
  useDeleteLearnTopicMutation,
  usePublishLearnSectionMutation,
  usePublishLearnTopicMutation,
  useReorderLearnSectionsMutation,
  useUnpublishLearnSectionMutation,
  useUnpublishLearnTopicMutation,
} from '@/client/api/learn';
import { getLearnIconComponent } from './components/LearnIconPicker';
import { TopicFormDialog } from './components/TopicFormDialog';
import { SortableSectionRow } from './components/SortableSectionRow';

const TopicDetailPage: React.FC = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();

  const {
    data: topic,
    isLoading,
    isError,
    refetch,
  } = useAdminLearnTopicQuery(topicId ?? null);

  useBreadcrumbs([
    { label: 'Content', href: '/admin/content/pages' },
    { label: 'Learn', href: '/admin/content/learn' },
    { label: topic?.title || topicId || 'Topic Details' },
  ]);

  const reorderSectionsMutation = useReorderLearnSectionsMutation();
  const publishTopicMutation = usePublishLearnTopicMutation();
  const unpublishTopicMutation = useUnpublishLearnTopicMutation();
  const deleteTopicMutation = useDeleteLearnTopicMutation();
  const publishSectionMutation = usePublishLearnSectionMutation();
  const unpublishSectionMutation = useUnpublishLearnSectionMutation();
  const deleteSectionMutation = useDeleteLearnSectionMutation();

  const [topicDialogOpen, setTopicDialogOpen] = useState(false);
  const [pendingDeleteSection, setPendingDeleteSection] = useState<LearnSectionSummary | null>(null);
  const [pendingDeleteTopic, setPendingDeleteTopic] = useState<boolean>(false);
  const [publishingSectionSlug, setPublishingSectionSlug] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const rawSections = topic?.sections || [];
  const sortedSections = useMemo(() => {
    return [...rawSections].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [rawSections]);

  const handleSectionDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !topic) return;

    const oldIndex = sortedSections.findIndex((s) => s.slug === active.id);
    const newIndex = sortedSections.findIndex((s) => s.slug === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newSectionsList = arrayMove(sortedSections, oldIndex, newIndex);
    const orderedSlugs = newSectionsList.map((s) => s.slug);

    try {
      await reorderSectionsMutation.mutateAsync({
        topicId: topic.id,
        order: orderedSlugs,
      });
      toast.success('Section ordering updated');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to reorder sections');
      refetch();
    }
  };

  const handleToggleTopicPublish = async () => {
    if (!topic) return;
    try {
      if (topic.published) {
        await unpublishTopicMutation.mutateAsync(topic.id);
        toast.success(`Unpublished topic "${topic.title}"`, {
          description: 'All sections in this topic are now hidden from public readers.',
        });
      } else {
        await publishTopicMutation.mutateAsync(topic.id);
        toast.success(`Published topic "${topic.title}"`);
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update topic status');
    }
  };

  const handleToggleSectionPublish = async (section: LearnSectionSummary) => {
    setPublishingSectionSlug(section.slug);
    try {
      if (section.published) {
        await unpublishSectionMutation.mutateAsync(section.slug);
        toast.success(`Unpublished section "${section.title}"`);
      } else {
        await publishSectionMutation.mutateAsync(section.slug);
        toast.success(`Published section "${section.title}"`);
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update section publish status');
    } finally {
      setPublishingSectionSlug(null);
    }
  };

  const handleDeleteSectionConfirm = async () => {
    if (!pendingDeleteSection || !topic) return;
    try {
      await deleteSectionMutation.mutateAsync({
        slug: pendingDeleteSection.slug,
        topicId: topic.id,
      });
      toast.success(`Deleted section "${pendingDeleteSection.title}"`);
      setPendingDeleteSection(null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete section');
    }
  };

  const handleDeleteTopicConfirm = async () => {
    if (!topic) return;
    if (sortedSections.length > 0) {
      toast.error('Cannot delete topic with sections', {
        description: `This topic has ${sortedSections.length} section(s). Move or delete them first.`,
      });
      setPendingDeleteTopic(false);
      return;
    }

    try {
      await deleteTopicMutation.mutateAsync({ id: topic.id });
      toast.success(`Deleted topic "${topic.title}"`);
      navigate('/admin/content/learn');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete topic');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-36 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (isError || !topic) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-2xl rounded-xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <AlertCircle className="mx-auto h-10 w-10 text-red-500" />
          <h2 className="mt-3 text-lg font-bold text-gray-900">Topic Not Found</h2>
          <p className="mt-1 text-sm text-gray-500">
            The topic &quot;{topicId}&quot; could not be found or failed to load.
          </p>
          <Button asChild className="mt-4 gap-2">
            <Link to="/admin/content/learn">
              <ArrowLeft className="h-4 w-4" /> Back to Learn Topics
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const IconComp = getLearnIconComponent(topic.icon);
  const isMonitored = topic.kind === 'monitored';
  const isPublished = Boolean(topic.published);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Navigation back bar */}
        <div>
          <Button variant="ghost" size="sm" asChild className="gap-1.5 text-gray-600 hover:text-gray-900">
            <Link to="/admin/content/learn">
              <ArrowLeft className="h-4 w-4" /> Back to Topics
            </Link>
          </Button>
        </div>

        {/* Topic Banner Card */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                <IconComp className="h-7 w-7" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl font-bold text-gray-900">{topic.title}</h1>
                  <span className="font-mono text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                    id: {topic.id}
                  </span>
                </div>

                <p className="text-sm text-gray-600 max-w-2xl">
                  {topic.description || <span className="italic text-gray-400">No description provided</span>}
                </p>

                <div className="flex items-center gap-2 pt-2 flex-wrap">
                  {isPublished ? (
                    <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                      Published
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-gray-200 bg-gray-50 text-gray-600">
                      Draft (Unpublished)
                    </Badge>
                  )}

                  {isMonitored ? (
                    <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 flex items-center gap-1">
                      <Lock className="h-3 w-3" /> Monitored Visibility
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700 flex items-center gap-1">
                      <Globe className="h-3 w-3" /> Open (Public)
                    </Badge>
                  )}

                  <Badge variant="secondary" className="capitalize">
                    Surface: {topic.surface || 'reference'}
                  </Badge>

                  <Badge variant="secondary">
                    Sort Order: {topic.sort_order ?? 0}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleTopicPublish}
                className="gap-1.5"
              >
                <Globe className="h-4 w-4 text-gray-500" />
                {isPublished ? 'Unpublish Topic' : 'Publish Topic'}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setTopicDialogOpen(true)}
                className="gap-1.5"
              >
                <Pencil className="h-4 w-4" />
                Edit Topic
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setPendingDeleteTopic(true)}
                className="text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Visibility Notice if Monitored */}
          {isMonitored && (
            <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
              <span>
                <strong>Monitored Topic Notice:</strong> Sections under monitored topics are hidden from the public /learn catalog. Change kind to &quot;Open&quot; to make this lane publicly discoverable.
              </span>
            </div>
          )}
        </div>

        {/* Sections Header & Table */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-gray-200 p-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Topic Sections ({sortedSections.length})
              </h2>
              <p className="text-xs text-gray-500">
                Drag rows to reorder. The order in this table directly determines the reader&apos;s previous/next navigation.
              </p>
            </div>

            <Button
              size="sm"
              asChild
              className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white shrink-0"
            >
              <Link to={`/admin/content/learn/sections/new?topic=${encodeURIComponent(topic.id)}`}>
                <Plus className="h-4 w-4" />
                Add Section
              </Link>
            </Button>
          </div>

          {sortedSections.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="mx-auto h-10 w-10 text-gray-300" />
              <p className="mt-3 text-sm font-medium text-gray-900">No sections in this topic yet</p>
              <p className="mt-1 text-xs text-gray-500 max-w-sm mx-auto">
                Create educational content articles, guide steps, or references under this topic.
              </p>
              <Button size="sm" asChild className="mt-4 gap-1.5">
                <Link to={`/admin/content/learn/sections/new?topic=${encodeURIComponent(topic.id)}`}>
                  <Plus className="h-4 w-4" />
                  Create First Section
                </Link>
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleSectionDragEnd}
              >
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/75 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                      <th className="py-3 pl-4 pr-2 w-12 text-center">Order</th>
                      <th className="py-3 px-4">Section & Slug</th>
                      <th className="py-3 px-4">Read Time</th>
                      <th className="py-3 px-4">Publish State</th>
                      <th className="py-3 pr-4 pl-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <SortableContext
                    items={sortedSections.map((s) => s.slug)}
                    strategy={verticalListSortingStrategy}
                  >
                    <tbody>
                      {sortedSections.map((section) => (
                        <SortableSectionRow
                          key={section.slug}
                          section={section}
                          topicId={topic.id}
                          onDelete={(s) => setPendingDeleteSection(s)}
                          onTogglePublish={handleToggleSectionPublish}
                          isPublishing={publishingSectionSlug === section.slug}
                        />
                      ))}
                    </tbody>
                  </SortableContext>
                </table>
              </DndContext>
            </div>
          )}
        </div>

        {/* Edit Topic Dialog */}
        <TopicFormDialog
          open={topicDialogOpen}
          onOpenChange={setTopicDialogOpen}
          topic={topic}
          onSuccess={() => refetch()}
        />

        {/* Delete Section Dialog */}
        <AlertDialog
          open={Boolean(pendingDeleteSection)}
          onOpenChange={(open) => !open && setPendingDeleteSection(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Section &quot;{pendingDeleteSection?.title}&quot;?</AlertDialogTitle>
              <AlertDialogDescription>
                This will soft-delete the section from &quot;{topic.title}&quot;. Old URLs will no longer resolve unless restored.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteSectionConfirm}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete Section
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Topic Dialog */}
        <AlertDialog
          open={pendingDeleteTopic}
          onOpenChange={setPendingDeleteTopic}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Topic &quot;{topic.title}&quot;?</AlertDialogTitle>
              <AlertDialogDescription>
                {sortedSections.length > 0 ? (
                  <span className="text-amber-700 font-medium block">
                    ⚠️ This topic has {sortedSections.length} section(s). You must move or delete them before this topic can be deleted.
                  </span>
                ) : (
                  'This will delete the topic. This action cannot be undone.'
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteTopicConfirm}
                disabled={sortedSections.length > 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              >
                Delete Topic
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default TopicDetailPage;
